import { useState, useRef } from 'react';
import { Pencil, RotateCcw, Trash2, Loader2, Eye, EyeOff, Plus, X, Check, AlertCircle } from 'lucide-react';
import adminAxios from '../lib/adminAxios';
import useProducts, { invalidateProducts } from '../hooks/useProducts';
import { invalidateProductImages } from '../hooks/useProductImages';
import EditProductDialog from './EditProductDialog';
import { getProductImage, SETS } from '../data/products';

const CATEGORY_OPTIONS = [
  'Wallets', 'Business Bags', 'Pens', 'Notebooks', 'Portfolios',
  'Travel', 'Gift Sets', 'Accessories', 'Other',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  code: '', name: '', category: '', dimensions: '',
  leather: '', description: '', features: [''],
  colors: [{ label: '', hex: '#1a1a1a', ref: '' }],
  set_slugs: [],
});

const Field = ({ label, value, onChange, placeholder, type = 'text', testid, required }) => (
  <div>
    <label className="mb-label">{label}{required && <span className="text-gold ml-1">*</span>}</label>
    <input
      type={type} className="mb-input" value={value}
      onChange={onChange} placeholder={placeholder}
      data-testid={testid} required={required}
    />
  </div>
);

// Merged Add + Edit catalogue panel.
export const EditCataloguePanel = ({ onChanged }) => {
  const { products, isCustom, hasOverride, refetch } = useProducts();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState({});

  // ── Add-form state ──────────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const fileRef = useRef();

  const list = Object.values(products).sort((a, b) => a.code.localeCompare(b.code));

  const onSaved = async () => {
    invalidateProducts();
    invalidateProductImages?.();
    await refetch();
    onChanged?.();
  };

  // ── Add-form handlers ───────────────────────────────────────────────────────
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ''] }));
  const setFeature = (i, v) => setForm((f) => { const a = [...f.features]; a[i] = v; return { ...f, features: a }; });
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, j) => j !== i) }));
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { label: '', hex: '#1a1a1a', ref: '' }] }));
  const setColor = (i, k, v) => setForm((f) => { const a = [...f.colors]; a[i] = { ...a[i], [k]: v }; return { ...f, colors: a }; });
  const removeColor = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, j) => j !== i) }));
  const toggleSet = (slug) => setForm((f) => ({
    ...f,
    set_slugs: f.set_slugs.includes(slug) ? f.set_slugs.filter((s) => s !== slug) : [...f.set_slugs, slug],
  }));
  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(f ? URL.createObjectURL(f) : null);
  };
  const resetForm = () => {
    setForm(emptyForm());
    setFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setAddResult(null);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    setAddResult(null);
    const fd = new FormData();
    fd.append('code', form.code);
    fd.append('name', form.name);
    fd.append('category', form.category);
    fd.append('dimensions', form.dimensions);
    fd.append('leather', form.leather);
    fd.append('description', form.description);
    fd.append('features', JSON.stringify(form.features.map((f) => f.trim()).filter(Boolean)));
    fd.append('colors', JSON.stringify(form.colors.filter((c) => c.label.trim()).map((c) => ({
      label: c.label.trim(), hex: c.hex.trim(), ref: c.ref.trim() || undefined,
    }))));
    fd.append('set_slugs', JSON.stringify(form.set_slugs));
    if (file) fd.append('image', file);
    try {
      const { data } = await adminAxios.post(`${API}/admin/products`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAddResult({ ok: true, msg: `'${data.code} · ${data.name}' added to catalogue.` });
      resetForm();
      setAddOpen(false);
      await onSaved();
    } catch (err) {
      setAddResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setAddBusy(false);
    }
  };

  // ── Existing row-action handlers ────────────────────────────────────────────
  const toggleVisibility = async (code, currentVisible) => {
    const newVisible = currentVisible === false ? true : false;
    setBusy((b) => ({ ...b, [code]: true }));
    try {
      await adminAxios.patch(`${API}/admin/products/${encodeURIComponent(code)}/visible?visible=${newVisible}`);
      await onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [code]: false }));
    }
  };

  const resetOne = async (code) => {
    if (!confirm(`Reset ${code} to its original catalogue data?`)) return;
    setBusy((b) => ({ ...b, [code]: true }));
    try {
      await adminAxios.delete(`${API}/admin/products/${encodeURIComponent(code)}/override`);
      await onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [code]: false }));
    }
  };

  const deleteOne = async (code) => {
    if (!confirm(`Delete ${code}? This also removes any uploaded photos.`)) return;
    setBusy((b) => ({ ...b, [code]: true }));
    try {
      await adminAxios.delete(`${API}/admin/products/${encodeURIComponent(code)}`);
      await onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [code]: false }));
    }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-catalogue-panel">
      {/* Header */}
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow text-gold mb-4">Catalogue</div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">Edit pieces. Add new ones.</h2>
        </div>
        <button
          className="btn-outline-gold"
          onClick={() => { setAddOpen((v) => !v); setAddResult(null); }}
          data-testid="admin-toggle-add"
        >
          {addOpen
            ? <><X size={14} strokeWidth={1.25} className="inline mr-1" />Close</>
            : <><Plus size={14} strokeWidth={1.25} className="inline mr-1" />New piece</>}
        </button>
      </div>

      {/* ── Add form (inline, collapsible) ───────────────────────────────── */}
      {addOpen && (
        <form
          onSubmit={submitAdd}
          data-testid="admin-add-form"
          className="bg-mb-surface hairline border-gold p-6 md:p-10 mb-10"
        >
          <div className="eyebrow text-gold mb-6">New piece</div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {/* Left column */}
            <div className="space-y-6">
              <Field label="Code" required testid="add-code"
                value={form.code} onChange={update('code')} placeholder="GV 5200" />
              <Field label="Name" required testid="add-name"
                value={form.name} onChange={update('name')} placeholder="Document holder" />

              <div>
                <label className="mb-label">Category<span className="text-gold ml-1">*</span></label>
                <select required className="mb-input" data-testid="add-category"
                  value={form.category} onChange={update('category')}
                  style={{ appearance: 'none' }}>
                  <option value="" style={{ background: '#2a0d1a', color: '#F0EDE5' }}>Select</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c} style={{ background: '#2a0d1a', color: '#F0EDE5' }}>{c}</option>
                  ))}
                </select>
              </div>

              <Field label="Dimensions" testid="add-dimensions"
                value={form.dimensions} onChange={update('dimensions')} placeholder="35 × 25 × 4 cm" />
              <Field label="Leather / material" testid="add-leather"
                value={form.leather} onChange={update('leather')} placeholder="Italian black" />

              <div>
                <label className="mb-label">Description</label>
                <textarea rows={3} className="mb-input" data-testid="add-description"
                  value={form.description} onChange={update('description')}
                  placeholder="Short editorial copy line." />
              </div>

              <div>
                <label className="mb-label">Add to set(s) — optional</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SETS.map((s) => {
                    const on = form.set_slugs.includes(s.slug);
                    return (
                      <button type="button" key={s.slug}
                        onClick={() => toggleSet(s.slug)}
                        data-testid={`add-set-${s.slug}`}
                        className={`feature-badge ${on ? '' : 'feature-badge--muted'}`}>
                        {on ? '●' : '○'} {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-6">
              <div>
                <label className="mb-label">Photo (optional)</label>
                <div className="mt-2 grid grid-cols-[120px_1fr] gap-4 items-start">
                  <div className="aspect-[4/5] hairline border-subtle bg-mb overflow-hidden flex items-center justify-center">
                    {filePreview
                      ? <img src={filePreview} alt="preview" className="w-full h-full object-cover" />
                      : <div className="eyebrow-sm text-mb-muted text-center px-2">No file</div>}
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
                      data-testid="add-image"
                      className="block w-full text-[13px] text-mb-muted
                        file:mr-4 file:py-2 file:px-4 file:hairline
                        file:border-gold file:bg-transparent file:text-gold
                        file:eyebrow-sm file:cursor-pointer hover:file:bg-mb-surface" />
                    <p className="mt-3 eyebrow-sm text-mb-muted">JPG, PNG or WebP.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-label">Features</label>
                <div className="space-y-2">
                  {form.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className="mb-input" value={f}
                        onChange={(e) => setFeature(i, e.target.value)}
                        placeholder="e.g. RFID + NFC" data-testid={`add-feature-${i}`} />
                      <button type="button" onClick={() => removeFeature(i)}
                        className="text-mb-muted hover:text-gold p-2">
                        <X size={14} strokeWidth={1.25} />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addFeature} className="mt-3 eyebrow text-gold">
                  + Add feature
                </button>
              </div>

              <div>
                <label className="mb-label">Colors / finishes</label>
                <div className="space-y-3">
                  {form.colors.map((c, i) => (
                    <div key={i} className="grid grid-cols-[40px_1fr_1fr_28px] gap-3 items-center">
                      <input type="color" value={c.hex}
                        onChange={(e) => setColor(i, 'hex', e.target.value)}
                        className="w-10 h-10 hairline border-subtle bg-transparent cursor-pointer"
                        data-testid={`add-color-hex-${i}`} />
                      <input className="mb-input" placeholder="Label (Black)" value={c.label}
                        onChange={(e) => setColor(i, 'label', e.target.value)}
                        data-testid={`add-color-label-${i}`} />
                      <input className="mb-input" placeholder="Ref (GV 5200-6)" value={c.ref}
                        onChange={(e) => setColor(i, 'ref', e.target.value)}
                        data-testid={`add-color-ref-${i}`} />
                      <button type="button" onClick={() => removeColor(i)}
                        className="text-mb-muted hover:text-gold p-1">
                        <X size={14} strokeWidth={1.25} />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addColor} className="mt-3 eyebrow text-gold">
                  + Add finish
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 hairline-t border-subtle pt-6 flex flex-wrap items-center gap-4">
            <button type="submit" className="btn-gold disabled:opacity-50"
              disabled={addBusy} data-testid="admin-add-submit">
              {addBusy ? <><Loader2 size={14} className="animate-spin inline mr-1" />Adding…</> : 'Add to catalogue'}
            </button>
            <button type="button" className="eyebrow text-mb-muted hover:text-gold" onClick={resetForm}>
              Reset form
            </button>
            {addResult && (
              <div className={`hairline px-4 py-2 ${addResult.ok ? 'border-gold' : 'border-subtle'}`}
                data-testid="admin-add-result">
                <div className={`flex items-center gap-2 eyebrow-sm ${addResult.ok ? 'text-gold' : 'text-mb-muted'}`}>
                  {addResult.ok ? <Check size={12} /> : <AlertCircle size={12} />}
                  {addResult.msg}
                </div>
              </div>
            )}
          </div>
        </form>
      )}

      {/* ── Product list ─────────────────────────────────────────────────────── */}
      <div className="hairline border-subtle">
        {/* Header row */}
        <div className="grid grid-cols-[60px_120px_minmax(160px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto]
                        md:gap-3 gap-2 items-center px-4 py-3 hairline-b border-subtle bg-mb-surface">
          <div className="eyebrow-sm text-mb-muted">Photo</div>
          <div className="eyebrow-sm text-mb-muted">Code</div>
          <div className="eyebrow-sm text-mb-muted">Name</div>
          <div className="eyebrow-sm text-mb-muted">Category</div>
          <div className="eyebrow-sm text-mb-muted">Status</div>
          <div />
        </div>

        {list.map((p) => {
          const custom = isCustom(p.code);
          const overridden = hasOverride(p.code);
          const isDraft = p.visible === false;
          const status = isDraft ? 'Draft'
            : custom ? 'Custom'
            : overridden ? 'Edited' : 'Original';
          const statusColor = isDraft ? 'text-mb-muted opacity-50'
            : (custom || overridden) ? 'text-gold' : 'text-mb-muted';
          return (
            <div
              key={p.code}
              className={`grid grid-cols-[60px_120px_minmax(160px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto]
                          md:gap-3 gap-2 items-center px-4 py-3 hairline-b border-subtle
                          hover:bg-mb-rich/30 transition-colors ${isDraft ? 'opacity-60' : ''}`}
              data-testid={`admin-edit-row-${p.code.replace(/\s/g, '-')}`}
            >
              <div className="w-[44px] h-[44px] hairline border-subtle bg-mb overflow-hidden">
                <img
                  src={getProductImage(p.code)}
                  alt={p.code}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = 0; }}
                />
              </div>
              <div className="eyebrow-sm text-gold">{p.code}</div>
              <div className="font-display text-[15px] text-mb">{p.name}</div>
              <div className="eyebrow-sm text-mb-muted">{p.category}</div>
              <div className={`eyebrow-sm ${statusColor}`}>{status}</div>
              <div className="flex items-center gap-1 justify-end">
                <button
                  className="text-mb hover:text-gold p-2"
                  onClick={() => setEditing(p)}
                  title="Edit"
                  data-testid={`admin-edit-${p.code.replace(/\s/g, '-')}`}
                >
                  <Pencil size={14} strokeWidth={1.25} />
                </button>
                {custom && (
                  <button
                    className="text-mb hover:text-gold p-2 disabled:opacity-30"
                    onClick={() => toggleVisibility(p.code, p.visible)}
                    disabled={busy[p.code]}
                    title={isDraft ? 'Publish to catalogue' : 'Set as draft'}
                    data-testid={`admin-visibility-${p.code.replace(/\s/g, '-')}`}
                  >
                    {busy[p.code]
                      ? <Loader2 size={14} className="animate-spin" />
                      : isDraft ? <Eye size={14} strokeWidth={1.25} /> : <EyeOff size={14} strokeWidth={1.25} />}
                  </button>
                )}
                {overridden && (
                  <button
                    className="text-mb hover:text-gold p-2 disabled:opacity-30"
                    onClick={() => resetOne(p.code)}
                    disabled={busy[p.code]}
                    title="Reset to original"
                    data-testid={`admin-edit-reset-${p.code.replace(/\s/g, '-')}`}
                  >
                    {busy[p.code]
                      ? <Loader2 size={14} className="animate-spin" />
                      : <RotateCcw size={14} strokeWidth={1.25} />}
                  </button>
                )}
                {custom && (
                  <button
                    className="text-mb hover:text-gold p-2 disabled:opacity-30"
                    onClick={() => deleteOne(p.code)}
                    disabled={busy[p.code]}
                    title="Delete"
                    data-testid={`admin-edit-delete-${p.code.replace(/\s/g, '-')}`}
                  >
                    {busy[p.code]
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Trash2 size={14} strokeWidth={1.25} />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditProductDialog
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onSaved(); }}
        />
      )}
    </section>
  );
};

export default EditCataloguePanel;
